import { cn } from "@app/lib/utils";
import { useInViewport } from "@design-system/hooks";
import { SARIcon } from "@design-system/icons";
import { reports } from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { CountingNumber } from "./CountingNumber";

const dotClassMap: Record<string, string> = {
  cerulean: "bg-aqua-blue",
  construction: "bg-construction",
};

const currencyIconMap = {
  sar: SARIcon,
};

type Report = (typeof reports)[0];
type MetricCardProps = {
  report: Report;
  isInView: boolean;
};

function MetricCard({ report, isInView }: MetricCardProps) {
  const CurrencyIcon = currencyIconMap[report.currencyIcon];

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-white/15 bg-white/[0.09] p-6 backdrop-blur-sm lg:p-8">
      <div className="relative flex items-center ps-4">
        <span
          className={cn(
            "absolute start-0 top-1/2 size-2 shrink-0 -translate-y-1/2 rounded-full",
            dotClassMap[report.dotColor] ?? "bg-aqua-blue",
          )}
        />
        <span className="text-sm font-semibold uppercase leading-normal tracking-wider text-white/80">
          {trans(report.label)}
        </span>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex items-center">
          <CurrencyIcon className="mb-1 size-10 shrink-0" />
          <CountingNumber number={report.number} isInView={isInView} />
        </div>
        {report.unit && (
          <span className="mb-1 text-lg font-semibold text-titanium-grey">
            {report.unit}
          </span>
        )}
        {report.suffix && (
          <span className="mb-1 text-lg font-semibold text-titanium-grey">
            {trans(report.suffix)}
          </span>
        )}
      </div>

      {report.change && (
        <div className="flex w-fit items-center gap-1.5 rounded-full bg-green-500/15 px-3 py-1">
          <span className="text-xs text-green-400">▲</span>
          <span className="text-xs font-medium text-green-400">
            {trans(report.change)}
          </span>
        </div>
      )}
    </div>
  );
}

export function QuarterEarnings() {
  const { ref, isInView } = useInViewport<HTMLDivElement>();
  const topReports = reports.slice(0, 4);

  return (
    <div ref={ref} className="bg-ir-band py-16 lg:py-24">
      <div className="container flex flex-col gap-8 lg:gap-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-white" />
              <span className="text-xs font-semibold uppercase tracking-widest text-white">
                {trans("financialPerformance")}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white lg:text-5xl">
              {trans("fullYearPerformanceOverview")}
            </h2>
          </div>

          <div className="shrink-0 rounded-md border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white">
            {trans("allFiguresAreForYear", { year: 2025 })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {topReports.map((report, index) => (
            <MetricCard key={index} report={report} isInView={isInView} />
          ))}
        </div>
      </div>
    </div>
  );
}
